import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  Grid2,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import usePost from "../../../services/hooks/usePost";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import { AlertType, UserRoles } from "../../../shared/constants/enums";
import { Button, Heading } from "../../../shared/ReusableComponents";
import CloseBtn from "../../../shared/ReusableComponents/CloseBtn";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { toast } from "../../../store/toastSlice";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";

export const AdminArchive = () => {
  const [archiveData, setArchiveData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // const [cohorts, setCohorts] = useState([]);
  const [cohortMap, setCohortMap] = useState<{ [key: number]: string }>({});
  const dispatch = useDispatch();

  const {
    data: getArchiveResponse,
    loading: getArchiveLoading,
    getService,
  } = useGet();
  const { data: getCohortResponse, getService: getCohortService } = useGet();
  const { data: postArchiveResponse, postService } = usePost();
  const roleIds = "3,4"; //3-student, 4-tutor
  const roleDisplayNames: { [key in UserRoles]: string } = {
    [UserRoles.SUPER_USER]: "Super User",
    [UserRoles.SUBJECT_COORDINATOR]: "Coordinator",
    [UserRoles.NURSING_STUDENT]: "Student",
    [UserRoles.TUTOR]: "Tutor",
  };

  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Student/Tutor Code",
    },
    {
      field: "userFirstName",
      headerName: "First Name",
    },
    {
      field: "userLastName",
      headerName: "Last Name",
    },
    {
      field: "userName",
      headerName: "Login ID",
    },
    {
      field: "emailId",
      headerName: "Email ID",
      valueGetter: (value, row) => (row.email ? row.email : ""),
    },
    {
      field: "roleName",
      headerName: "Role Name",
      resizable: false,
      valueGetter: (value, row) =>
        row.roleId ? roleDisplayNames[row.roleId as UserRoles] : "",
    },
    {
      field: "cohortName",
      headerName: "Cohort Name",
      resizable: false,
      valueGetter: (value, row) => {
        const cohortName = cohortMap[row.cohortId];
        return CommonFunctions.getDisplayValue(cohortName);
      },
    },
    {
      field: "status",
      headerName: "Status",
      resizable: false,
      valueGetter: (params, row) =>
        row.statusId === 1 ? "Active" : "Inactive",
    },
  ];

  const openDialog = () => {
    if (archiveData.length === 0) {
      dispatch(
        toast({
          message:
            "All Data have been archived, please upload new Student & tutor list to archive.",
          type: AlertType.INFO,
        })
      );
    } else {
      setDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);

    try {
      const payload = {};
      await postService(apiEndpoints.MASTER_ARCHIVE, payload);
      setDialogOpen(false);
    } catch (error) {
      dispatch(
        toast({
          message: "Archiving failed. Please try again.",
          type: AlertType.ERROR,
        })
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    if (postArchiveResponse) {
      dispatch(
        toast({
          message: "All Student and Tutor Data has been Successfully Archived.",
          type: AlertType.SUCCESS,
        })
      );
      getService(`${apiEndpoints.USERS}?commaSeparatedRoleIds=${roleIds}`);
    }
  }, [postArchiveResponse]);

  useEffect(() => {
    if (getArchiveLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [getArchiveLoading]);

  useEffect(() => {
    if (!getArchiveResponse || !getCohortResponse) {
      const roleIds = "3,4"; //3-student, 4-tutor
      getService(`${apiEndpoints.USERS}?commaSeparatedRoleIds=${roleIds}`);
      getCohortService(apiEndpoints.COHORT);
    }
    if (getArchiveResponse && getCohortResponse) {
      setArchiveData(getArchiveResponse);
      // setCohorts(getCohortResponse);

      const cohortMap = getCohortResponse.reduce((acc: any, cohort: any) => {
        acc[cohort.id] = cohort.cohortName;
        return acc;
      }, {});
      setCohortMap(cohortMap);
    }
  }, [getArchiveResponse]);

  const handleCancel = () => {
    if (!confirmLoading) setDialogOpen(false);
  };
  return (
    <Grid2 sx={{ marginLeft: "5px", marginRight: "16px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 0px",
        }}
      >
        <Heading type={"h3"} text="Archive" />
        <IconButton
          onClick={openDialog}
          className="archive-btn"
          text="Archive"
          icon={
            <span
              className="material-symbols-outlined"
              style={{ marginRight: "8px" }}
            >
              archive
            </span>
          }
        />
      </Box>
      <div style={{ backgroundColor: "white" }}>
        <Heading
          text="Student and Tutor"
          type="h5"
          style={{ marginLeft: "20px", padding: "20px 0" }}
          isBold
        />
        <GridTable
          rows={archiveData}
          columns={columns}
          pageSizeOptions={[5, 10, 15]}
          initialPageSize={10}
        />
        <Dialog
          open={dialogOpen}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown={confirmLoading}
          onClose={(event, reason) => {
            // Prevent closing only when loading is true
            if (confirmLoading) return;

            // Allow close on backdrop click and Escape when not loading
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              handleCancel();
            }
          }}
        >
          <Box
            sx={{
              padding: "26px 0px 0px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <CloseBtn onClose={handleCancel} />

            <Box sx={{ textAlign: "center", color: "warning.main" }}>
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "60px" }}
              >
                warning
              </span>
            </Box>
            <Heading
              text="Warning: You're about to archive all existing data. This action cannot be undone."
              type="h5"
              isBold
            />
            <Heading text="Do you wish to continue?" type="h5" isBold />

            <DialogActions
              sx={{
                m: "20px 0px 22px 0px",
                display: "flex",
                justifyContent: "center",
                columnGap: "20px",
                alignItems: "center",
              }}
            >
              {confirmLoading ? (
                <CircularProgress size={40} />
              ) : (
                <React.Fragment>
                  <Button
                    text="Cancel"
                    fullWidth={false}
                    variant="outlined"
                    type="button"
                    onClick={handleCancel}
                  />
                  <Button
                    text="Confirm"
                    fullWidth={false}
                    variant="contained"
                    className="ml-1"
                    onClick={handleConfirm}
                  />
                </React.Fragment>
              )}
            </DialogActions>
          </Box>
        </Dialog>
      </div>
    </Grid2>
  );
};
